import {
	isObjectType,
	isInterfaceType,
	isUnionType,
	isEnumType,
	isScalarType,
	isInputObjectType,
	isListType,
	isNonNullType,
	type GraphQLType,
	type GraphQLSchema,
} from 'graphql';

import type { TypeRef, TypesByName, TypeEntry, FieldDef, FieldArg } from './GraphQLSchemaAtoms';

function toTypeRef(t: GraphQLType): TypeRef {
	if (isNonNullType(t)) {
		return { kind: 'NON_NULL', ofType: toTypeRef(t.ofType) };
	}
	if (isListType(t)) {
		return { kind: 'LIST', ofType: toTypeRef(t.ofType) };
	}
	// Named types
	const named: any = t as any;
	const name = named?.name ?? 'Unknown';
	return { kind: 'NAMED', name };
}

export function toTypesByNameFromSchema(schema: GraphQLSchema): TypesByName {
	const typeMap = schema.getTypeMap();
	const result: TypesByName = {};

	for (const [typeName, gqlType] of Object.entries(typeMap)) {
		if (typeName.startsWith('__')) {
			continue;
		}

		const entry: TypeEntry = {
			name: typeName,
			kind: isObjectType(gqlType)
				? 'OBJECT'
				: isInterfaceType(gqlType)
					? 'INTERFACE'
					: isUnionType(gqlType)
						? 'UNION'
						: isEnumType(gqlType)
							? 'ENUM'
							: isScalarType(gqlType)
								? 'SCALAR'
								: isInputObjectType(gqlType)
									? 'INPUT_OBJECT'
									: 'UNKNOWN',
			description: (gqlType as any).description,
		};

		if (isObjectType(gqlType)) {
			const fieldsMap = gqlType.getFields();
			const fields: FieldDef[] = Object.values(fieldsMap).map((f: any) => {
				const args: FieldArg[] | undefined = Array.isArray(f.args)
					? f.args.map((a: any) => ({
							name: a.name,
							description: a.description,
							type: toTypeRef(a.type),
							defaultValue: a.defaultValue ?? null,
							isDeprecated: Boolean(a.deprecationReason),
							deprecationReason: a.deprecationReason ?? null,
						}))
					: undefined;
				return {
					name: f.name,
					description: f.description,
					type: toTypeRef(f.type),
					args,
					isDeprecated: Boolean(f.deprecationReason),
					deprecationReason: f.deprecationReason ?? null,
				};
			});
			entry.fields = fields;
			entry.interfaces = gqlType.getInterfaces().map((i) => i.name);
		}

		if (isInterfaceType(gqlType)) {
			const fieldsMap = gqlType.getFields();
			const fields: FieldDef[] = Object.values(fieldsMap).map((f: any) => {
				const args: FieldArg[] | undefined = Array.isArray(f.args)
					? f.args.map((a: any) => ({
							name: a.name,
							description: a.description,
							type: toTypeRef(a.type),
							defaultValue: a.defaultValue ?? null,
							isDeprecated: Boolean(a.deprecationReason),
							deprecationReason: a.deprecationReason ?? null,
						}))
					: undefined;
				return {
					name: f.name,
					description: f.description,
					type: toTypeRef(f.type),
					args,
					isDeprecated: Boolean(f.deprecationReason),
					deprecationReason: f.deprecationReason ?? null,
				};
			});
			entry.fields = fields;
			const possibleTypes = schema.getPossibleTypes(gqlType);
			entry.possibleTypes = possibleTypes?.map((t) => t.name) ?? [];
		}

		if (isUnionType(gqlType)) {
			entry.possibleTypes = gqlType.getTypes().map((t) => t.name);
		}

		if (isEnumType(gqlType)) {
			entry.values = gqlType.getValues().map((v) => ({
				name: v.name,
				description: v.description,
				isDeprecated: v.isDeprecated,
				deprecationReason: v.deprecationReason ?? null,
			}));
		}

		if (isInputObjectType(gqlType)) {
			const fields = Object.values(gqlType.getFields()).map((f: any) => ({
				name: f.name,
				description: f.description,
				inputType: toTypeRef(f.type),
				isDeprecated: Boolean(f.deprecationReason),
				deprecationReason: f.deprecationReason ?? null,
			}));
			entry.fields = fields;
		}

		result[typeName.toLowerCase()] = entry;
	}

	return result;
}
